import { TextField, PhoneField, CountryCodeItem } from '@wheely/ui-kit';
import { IDENTIFIER_TYPES, OTP_TRANSPORTS, type IdentifierType } from '@wheely/web-auth-lib/core';
import { OTP_FLOW_PHASES, useOtpChallenge } from '@wheely/web-auth-lib/react';
import React, { useState, useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PHONE_CODES } from '@/shared/const/phone-codes';
import { useDismissibleError } from '@/shared/hooks/useDismissibleError';
import { getAuthErrorText } from '@/shared/utils/getAuthErrorText';
import { getInitialPhoneCodeByLocale } from '@/shared/utils/getInitialPhoneCodeByLocale';
import { normalizePossiblePhoneNumber } from '@/shared/utils/phoneUtils';
import { ChangeButton } from '@/shared/view/ChangeButton';
import { CodeForm } from '@/shared/view/CodeForm';
import { LinkButton } from '@/shared/view/LinkButton';
import { Button } from '@src-common/view/adapters/Button';

import { Captcha, CaptchaHandle } from '../Captcha/Captcha';

import s from './style.module.css';

type Props = {
  initialEmail?: string;
  /** No account for this identifier: the parent offers registration. */
  onRegister: (email: string) => void;
};

/**
 * Sign-in by either identifier. The phone is offered first: every account has
 * one, while an email is attached only to accounts made since the email-first
 * release. Email stays one click away, and is where the form opens when the
 * registration hands over an address that already has an account.
 */
const SignInForm: React.VFC<Props> = ({ initialEmail = '', onRegister }) => {
  const intl = useIntl();
  const [identifierType, setIdentifierType] = useState<IdentifierType>(
    initialEmail ? IDENTIFIER_TYPES.EMAIL : IDENTIFIER_TYPES.PHONE,
  );
  const [email, setEmail] = useState(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState(getInitialPhoneCodeByLocale(intl.locale));
  const [phoneValidationError, setPhoneValidationError] = useState<string | undefined>(undefined);
  const [unknownIdentifier, setUnknownIdentifier] = useState<
    { value: string; type: IdentifierType } | undefined
  >(undefined);
  const captchaRef = useRef<CaptchaHandle>(null);
  // The challenge keeps its error until the next start(), so the line under the
  // field outlives the value it described. Correcting the field drops it.
  const { isDismissed, dismiss, restore } = useDismissibleError();

  const otp = useOtpChallenge({
    // Paid transports only: the letter never asks for a captcha.
    // The soft sitekey only while upstream reports a budget left; a spent one,
    // or none reported at all, gets the strict sitekey.
    getCaptchaToken: (_transport, { invisibleCaptchaBudget }) =>
      captchaRef.current?.getCaptcha((invisibleCaptchaBudget ?? 0) <= 0) ??
      Promise.resolve(undefined),
    onUnknownAccount: (value, type) => setUnknownIdentifier({ value, type }),
  });

  const isPhone = identifierType === IDENTIFIER_TYPES.PHONE;
  const busy = otp.phase === OTP_FLOW_PHASES.STARTING || otp.phase === OTP_FLOW_PHASES.VERIFYING;
  const errorText = otp.error ? getAuthErrorText(intl, otp.error) : undefined;

  const switchIdentifierType = useCallback(
    (next: IdentifierType) => {
      if (busy) {
        return;
      }

      setIdentifierType(next);
      setUnknownIdentifier(undefined);
      setPhoneValidationError(undefined);
      otp.reset();
    },
    [busy, otp],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setUnknownIdentifier(undefined);
      restore();

      if (!isPhone) {
        otp.start({ identifier: email, identifierType: IDENTIFIER_TYPES.EMAIL });

        return;
      }

      // Only obvious junk is stopped here: the account behind the number may
      // predate any check, so its shape is the server's call, not ours.
      const phone = normalizePossiblePhoneNumber(countryCode + phoneNumber);

      if (!phone) {
        setPhoneValidationError(
          intl.formatMessage({
            defaultMessage: 'Please enter a valid phone number.',
            id: 'login_error_invalid_phone',
            description: 'Phone validation error',
          }),
        );

        return;
      }

      setPhoneValidationError(undefined);
      otp.start({ identifier: phone, identifierType: IDENTIFIER_TYPES.PHONE });
    },
    [isPhone, otp, email, countryCode, phoneNumber, intl, restore],
  );

  const handleChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setUnknownIdentifier(undefined);
      dismiss();
    },
    [dismiss],
  );

  const handleChangePhone = useCallback(
    (next: string) => {
      setPhoneNumber(next);
      setPhoneValidationError(undefined);
      setUnknownIdentifier(undefined);
      dismiss();
    },
    [dismiss],
  );

  const handleChangeCountryCode = useCallback(
    (code: CountryCodeItem) => {
      setCountryCode(code.value);
      setPhoneValidationError(undefined);
      setUnknownIdentifier(undefined);
      dismiss();
    },
    [dismiss],
  );

  if (otp.phase === OTP_FLOW_PHASES.FAILED) {
    return (
      <div className={s.signInForm} data-test-id="sign-in-form__failed">
        <p>{errorText}</p>
        <Button onClick={otp.reset} type="button">
          <FormattedMessage
            defaultMessage="Start over"
            id="login_start_over"
            description="Start over button title"
          />
        </Button>
      </div>
    );
  }

  if (otp.phase === OTP_FLOW_PHASES.DONE) {
    return null;
  }

  const isCodeStep =
    otp.phase === OTP_FLOW_PHASES.AWAITING_CODE || otp.phase === OTP_FLOW_PHASES.VERIFYING;

  // The unknown identifier is not a failure: the field keeps its value and the
  // error line offers a way on. An unknown address most often means an account
  // that predates email sign-in, so the phone comes first and registration second.
  const unknownAccountError = unknownIdentifier ? (
    <span data-test-id="sign-in-form__unknown-account">
      <FormattedMessage
        defaultMessage="Account not found."
        id="login_unknown_account_not_found"
        description="Unknown account error"
      />{' '}
      {unknownIdentifier.type === IDENTIFIER_TYPES.EMAIL && (
        <>
          <LinkButton onClick={() => switchIdentifierType(IDENTIFIER_TYPES.PHONE)}>
            <FormattedMessage
              defaultMessage="Sign in with your phone number"
              id="login_unknown_account_use_phone"
              description="Unknown account phone sign-in link"
            />
          </LinkButton>{' '}
          <FormattedMessage
            defaultMessage="or"
            id="login_unknown_account_or"
            description="Separator between the two ways out of an unknown account"
          />{' '}
        </>
      )}
      <LinkButton onClick={() => onRegister(isPhone ? '' : unknownIdentifier.value)}>
        <FormattedMessage
          defaultMessage="Register"
          id="login_unknown_account_register"
          description="Unknown account register link"
        />
      </LinkButton>
    </span>
  ) : undefined;

  const fieldError = unknownAccountError ?? (isCodeStep || isDismissed ? undefined : errorText);
  const sentTo = isPhone ? countryCode + phoneNumber : email;

  return (
    <div className={s.signInForm}>
      <Captcha ref={captchaRef} />
      <form onSubmit={handleSubmit} data-test-id="sign-in-form" noValidate={true}>
        <div className={s.field}>
          {isPhone ? (
            <PhoneField
              autoFocus={!isCodeStep}
              value={phoneNumber}
              title={
                <FormattedMessage
                  defaultMessage="Phone Number"
                  id="login_phone_title"
                  description="Phone field label"
                />
              }
              description={
                isCodeStep ? undefined : (
                  <FormattedMessage
                    defaultMessage="We will send you a sign-in code to this phone number."
                    id="login_phone_description"
                    description="Phone sign-in field lead"
                  />
                )
              }
              countryCode={countryCode}
              items={PHONE_CODES}
              placeholder={intl.formatMessage({
                description: 'Phone field placeholder',
                defaultMessage: 'Phone number',
                id: 'pTNKg/',
              })}
              onChange={handleChangePhone}
              onCodeChange={handleChangeCountryCode}
              disabled={busy || isCodeStep}
              error={phoneValidationError ?? fieldError}
            />
          ) : (
            <TextField
              type="email"
              value={email}
              title={
                <FormattedMessage
                  defaultMessage="Email"
                  id="login_email_title"
                  description="Email field label"
                />
              }
              description={
                isCodeStep ? undefined : (
                  <FormattedMessage
                    description="Email field label"
                    defaultMessage="We will send you a sign-in code to this email."
                    id="login_email_description"
                  />
                )
              }
              onChange={handleChangeEmail}
              placeholder={intl.formatMessage({
                description: 'Email field placeholder',
                defaultMessage: 'Email',
                id: 'login_email_placeholder',
              })}
              disabled={busy || isCodeStep}
              error={fieldError}
              autoFocus={!isCodeStep}
            />
          )}
          {isCodeStep && (
            <ChangeButton disabled={busy} onClick={otp.reset}>
              {isPhone ? (
                <FormattedMessage
                  defaultMessage="Change Phone Number"
                  id="login_change_phone"
                  description="Change phone action"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Change Email"
                  id="login_change_email"
                  description="Change email action"
                />
              )}
            </ChangeButton>
          )}
        </div>
        {!isCodeStep && (
          <div className={s.submit}>
            <Button isDisabled={busy || (isPhone ? !phoneNumber : !email)} type="submit">
              <FormattedMessage
                defaultMessage="Continue"
                id="global_continue"
                description="Continue button title"
              />
            </Button>
          </div>
        )}
      </form>
      {/* Accounts made before email sign-in have no address attached, so the
          other identifier stays reachable without hitting an error first. */}
      {!isCodeStep && (
        <p className={s.switch} data-test-id="sign-in-form__switch">
          <LinkButton
            onClick={() =>
              switchIdentifierType(isPhone ? IDENTIFIER_TYPES.EMAIL : IDENTIFIER_TYPES.PHONE)
            }
          >
            {isPhone ? (
              <FormattedMessage
                defaultMessage="Sign in with email"
                id="login_switch_to_email"
                description="Switch the sign-in to email"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Sign in with phone number"
                id="login_switch_to_phone"
                description="Switch the sign-in to phone"
              />
            )}
          </LinkButton>
        </p>
      )}
      {isCodeStep && (
        <CodeForm
          description={
            <FormattedMessage
              defaultMessage="Enter the code we sent to {identifier}."
              id="login_code_description"
              description="Sign-in code field label"
              values={{ identifier: <b>{sentTo}</b> }}
            />
          }
          disabled={busy}
          error={errorText}
          attemptsLeft={otp.attemptsLeft}
          resendAvailableIn={otp.resendAvailableIn}
          onSubmit={code => otp.submit(code)}
          // Named, not defaulted: without a transport the challenge repeats the
          // last one it used, so a single Call me would turn every later Resend
          // into another call.
          onResend={() => otp.resend(otp.transports?.preferredTransport)}
          onCallMe={
            otp.transports?.allowedTransports.includes(OTP_TRANSPORTS.AUTO_CALL)
              ? () => otp.resend(OTP_TRANSPORTS.AUTO_CALL)
              : undefined
          }
        />
      )}
    </div>
  );
};

export { SignInForm };
